import './objectHasOwnPolyfill.js';
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import { cs, de, pl, sk } from 'intl-tel-input/locale';

export const MARKET_PHONE_OPTIONS = {
    cz: ['cz', 'sk', 'pl', 'ua', 'de', 'gb', 'us', 'ca'],
    sk: ['sk', 'cz', 'pl', 'ua', 'de', 'hu', 'at', 'gb', 'us', 'ca'],
    pl: ['pl', 'ua', 'de', 'cz', 'sk', 'by', 'gb', 'us', 'ca'],
    de: ['de', 'ua', 'pl', 'ro', 'it', 'at', 'ch', 'cz', 'gb', 'us', 'ca'],
};

const translations = {cs, de, sk, pl};
const marketLocales = {cz: 'cs', de: 'de', pl: 'pl', sk: 'sk'};
const missingDialCodeMessages = {
    cz: 'Vyberte předvolbu země.',
    sk: 'Vyberte predvoľbu krajiny.',
    pl: 'Wybierz numer kierunkowy kraju.',
    de: 'Wählen Sie eine Ländervorwahl.',
};
const phoneFeedback = new WeakMap();
let phoneFeedbackId = 0;
const submissionAttempts = new WeakMap();
const pendingInputValues = new WeakMap();
const rawPhoneValues = new WeakMap();
const submissionMessages = {
    cz: {length: 'Zkontrolujte délku čísla a předvolbu.', prefix: 'Za předvolbou +420 nebo +421 zadejte číslo bez úvodní nuly a bez opakované předvolby země.'},
    sk: {length: 'Skontrolujte dĺžku čísla a predvoľbu.', prefix: 'Za predvoľbou +420 alebo +421 zadajte číslo bez úvodnej nuly a bez opakovanej predvoľby krajiny.'},
    pl: {length: 'Sprawdź długość numeru i numer kierunkowy.', prefix: 'Po prefiksie +420 lub +421 wpisz numer bez początkowego zera i bez powtarzania prefiksu kraju.'},
    de: {length: 'Prüfen Sie die Länge der Telefonnummer und die Vorwahl.', prefix: 'Geben Sie nach +420 oder +421 die Nummer ohne führende Null und ohne wiederholte Ländervorwahl ein.'},
};
const autofillChecks = new WeakMap();
const correctionBaselines = new WeakMap();
const phoneValueSnapshots = new WeakMap();
const phoneInputHandlers = new WeakMap();
const fallbackPhoneInputs = new WeakSet();
const selectedPhoneCountries = new WeakMap();
let fullscreenViewportListenersBound = false;
let fullscreenViewportUpdateScheduled = false;

export function fullscreenViewportInsets(layoutHeight, viewportTop, viewportHeight) {
    const top = Math.max(0, viewportTop);
    return {
        top,
        bottom: Math.max(0, layoutHeight - top - viewportHeight),
    };
}

function updateFullscreenCountrySelectorViewport() {
    fullscreenViewportUpdateScheduled = false;
    const viewport = window.visualViewport;
    if (!viewport) {
        return;
    }

    document.querySelectorAll('.iti--fullscreen-popup.iti--detached-country-selector:not(.iti__hide)').forEach(popup => {
        const {top, bottom} = fullscreenViewportInsets(popup.offsetHeight, viewport.offsetTop, viewport.height);
        popup.style.setProperty('--iti-viewport-top-inset', `${top}px`);
        popup.style.setProperty('--iti-viewport-bottom-inset', `${bottom}px`);
    });
}

function scheduleFullscreenCountrySelectorViewportUpdate() {
    if (fullscreenViewportUpdateScheduled || !window.visualViewport) {
        return;
    }
    fullscreenViewportUpdateScheduled = true;
    window.requestAnimationFrame(updateFullscreenCountrySelectorViewport);
}

function bindFullscreenCountrySelectorViewportListeners() {
    if (fullscreenViewportListenersBound || !window.visualViewport) {
        return;
    }
    fullscreenViewportListenersBound = true;
    document.addEventListener('click', event => {
        if (event.target?.closest?.('.iti__selected-country')) {
            scheduleFullscreenCountrySelectorViewportUpdate();
        }
    });
    document.addEventListener('focusin', event => {
        if (event.target?.closest?.('.iti--fullscreen-popup .iti__search-input')) {
            scheduleFullscreenCountrySelectorViewportUpdate();
        }
    });
    window.visualViewport.addEventListener('resize', scheduleFullscreenCountrySelectorViewportUpdate);
    window.visualViewport.addEventListener('scroll', scheduleFullscreenCountrySelectorViewportUpdate);
}

function setPhoneValidity(input, message = '') {
    input.setCustomValidity(message);
    input.classList?.toggle('phone-input-invalid', Boolean(message));
    let feedback = phoneFeedback.get(input);
    if (message) {
        if (!feedback) {
            const node = (input.ownerDocument ?? document).createElement('div');
            node.id = `phone-input-error-${++phoneFeedbackId}`;
            node.className = 'phone-input-error';
            node.setAttribute('role', 'alert');
            feedback = {node, previousInvalid: input.getAttribute('aria-invalid')};
            phoneFeedback.set(input, feedback);
            (input.closest('.iti') ?? input).insertAdjacentElement('afterend', node);
            const descriptions = input.getAttribute('aria-describedby');
            input.setAttribute('aria-describedby', [descriptions, node.id].filter(Boolean).join(' '));
        }
        feedback.node.textContent = message;
        input.setAttribute('aria-invalid', 'true');
    } else if (feedback) {
        const descriptions = (input.getAttribute('aria-describedby') ?? '').split(/\s+/)
            .filter(id => id && id !== feedback.node.id).join(' ');
        if (descriptions) {
            input.setAttribute('aria-describedby', descriptions);
        } else {
            input.removeAttribute('aria-describedby');
        }
        if (feedback.previousInvalid !== null) {
            input.setAttribute('aria-invalid', feedback.previousInvalid);
        } else {
            input.removeAttribute('aria-invalid');
        }
        feedback.node.remove();
        phoneFeedback.delete(input);
    }
}

// The installed utils.isValidNumber checks possible lengths only (Precise checks prefixes).
export function phoneSubmissionError(value, country, utils = null) {
    const dialCode = country?.dialCode;
    const inputValue = String(value ?? '');
    const national = dialCode && inputValue.trimStart().startsWith(`+${dialCode}`)
        ? inputValue.trimStart().slice(dialCode.length + 1) : inputValue;
    const digits = national.replace(/\D/g, '');
    if (['cz', 'sk'].includes(country?.iso2) && (digits.startsWith('0') || digits.startsWith(dialCode))) {
        return 'prefix';
    }
    if (!digits) {
        return 'length';
    }
    if (utils && !utils.isValidNumber(`+${dialCode}${digits}`, country.iso2, ['MOBILE', 'FIXED_LINE'])) {
        return 'length';
    }
    return null;
}

function recordSubmissionAttempt(input, value, inputType = null) {
    const country = intlTelInput.getInstance(input)?.getSelectedCountry();
    let reason = phoneSubmissionError(value, country, intlTelInput.utils);
    const previous = submissionAttempts.get(input);
    const nationalValue = country?.dialCode && value.trimStart().startsWith(`+${country.dialCode}`)
        ? value.trimStart().slice(country.dialCode.length + 1) : value;
    const nationalDigits = nationalValue.replace(/\D/g, '');
    const normalizedTrunkDigits = nationalDigits.startsWith('0') ? nationalDigits.slice(1) : '';
    if (reason === 'prefix' && ['cz', 'sk'].includes(country?.iso2) && nationalDigits.startsWith('0')) {
        if (normalizedTrunkDigits.startsWith(country.dialCode)) {
            // A trunk zero can expose a duplicated country prefix after normalization.
            reason = 'prefix';
        } else if (normalizedTrunkDigits.length > 9) {
            // Preserve an overlength attempt if strict formatting could clip it to a plausible value.
            reason = 'length';
        } else {
            // The formatter intentionally removes this one trunk zero; the visible value is authoritative.
            submissionAttempts.delete(input);
            return;
        }
    }
    // Keep errors whose invalid raw prefix/length could be hidden by formatting truncation.
    const previousDigitCount = (phoneValueSnapshots.get(input) ?? '').replace(/\D/g, '').length;
    if (!reason && ['cz', 'sk'].includes(country?.iso2) && previous?.iso2 === country.iso2 && previous?.reason === 'prefix' && inputType === 'insertText'
        && value.replace(/\D/g, '').length >= previousDigitCount) {
        return;
    }
    if (reason || !intlTelInput.utils) {
        submissionAttempts.set(input, {reason, value, iso2: country?.iso2});
    } else {
        submissionAttempts.delete(input);
    }
}

export function phoneSelectionKey(value, country) {
    return `${country?.iso2 ?? ''}:${value.replace(/\D/g, '')}`;
}

export function shouldCaptureRawPhoneInput(inputType, eventData, previousValue, currentValue) {
    if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop' || inputType === 'insertReplacementText') {
        return true;
    }

    const previousDigits = String(previousValue ?? '').replace(/\D/g, '').length;
    const currentDigits = String(currentValue ?? '').replace(/\D/g, '').length;
    return eventData == null && currentDigits - previousDigits > 1;
}

function updateCorrectionSubmit(input, country) {
    const baseline = correctionBaselines.get(input);
    if (baseline === undefined) {
        return;
    }
    const submit = input.closest('.modal-fix-phone')?.querySelector('input[type="submit"]');
    if (submit) {
        submit.disabled = phoneSelectionKey(input.value, country) === baseline;
    }
}

function startAutofillCheck(event) {
    const input = event.currentTarget;
    if (autofillChecks.has(input)) {
        return;
    }
    formatAndTrackPhoneInput(event, true);
    autofillChecks.set(input, window.setInterval(() => formatAndTrackPhoneInput({currentTarget: input}, true), 150));
}

function stopAutofillCheck(event) {
    const input = event.currentTarget;
    window.clearInterval(autofillChecks.get(input));
    autofillChecks.delete(input);
    formatAndTrackPhoneInput(event, true);
}

function captureRawPhoneInput(input, inputType = null, eventData = null) {
    const previousValue = phoneValueSnapshots.get(input) ?? '';
    if (!shouldCaptureRawPhoneInput(inputType, eventData, previousValue, input.value)) {
        return;
    }
    const originalPhoneInput = input.form?.querySelector('input[name="phone_original"], input[name$="[phone_original]"]');
    if (originalPhoneInput) {
        originalPhoneInput.value = input.value;
    }
    rawPhoneValues.set(input, inputType === 'insertFromPaste' && pendingInputValues.has(input)
        ? pendingInputValues.get(input) : input.value);
}

function normalizePhoneFormData(input, event) {
    if (fallbackPhoneInputs.has(input)) {
        return;
    }
    const instance = fallbackPhoneInputs.has(input) ? null : intlTelInput.getInstance(input);
    const country = getPhoneCountry(input, instance);
    if (!input.name || !country?.dialCode) {
        return;
    }
    const number = instance && intlTelInput.utils
        ? (instance.getNumber(intlTelInput.NUMBER_FORMAT.E164) || numberWithDialCode(input.value, country))
        : numberWithDialCode(input.value, country);
    if (number) {
        event.formData.set(input.name, number);
    }
}

function getPhoneCountry(input, instance = null) {
    if (instance) {
        const country = instance.getSelectedCountry();
        if (country?.dialCode) {
            selectedPhoneCountries.set(input, country);
            return country;
        }
        return country;
    }
    return selectedPhoneCountries.get(input) ?? null;
}

function fullPhoneValuePreservingDigits(value, country) {
    const raw = String(value ?? '').trim();
    if (!country?.dialCode || raw.startsWith('+')) return raw;
    const digits = raw.replace(/\D/g, '');
    if (!digits) return raw;
    return `+${country.dialCode}${digits}`;
}

function enablePlainPhoneFallback(input) {
    if (fallbackPhoneInputs.has(input)) {
        return selectedPhoneCountries.get(input) ?? null;
    }
    fallbackPhoneInputs.add(input);
    let country = selectedPhoneCountries.get(input) ?? null;
    try {
        const instance = intlTelInput.getInstance(input);
        country = getPhoneCountry(input, instance) ?? country;
    } catch {
        // Retain the last selected country when the widget API has failed.
    }
    const rawValue = rawPhoneValues.get(input) ?? input.value;
    const originalPhoneInput = input.form?.querySelector('input[name="phone_original"], input[name$="[phone_original]"]');
    if (originalPhoneInput && rawValue) {
        originalPhoneInput.value = rawValue;
    }
    const internationalValue = fullPhoneValuePreservingDigits(rawValue, country);
    const handlers = phoneInputHandlers.get(input);
    if (handlers) {
        if (handlers.captureOriginalInput) input.removeEventListener('input', handlers.captureOriginalInput, true);
        if (handlers.capturePasteAttempt) input.removeEventListener('paste', handlers.capturePasteAttempt, true);
        if (handlers.captureOriginalInput) input.removeEventListener('input', formatAndTrackPhoneInput);
        if (handlers.finishInputAttempt) input.removeEventListener('input', handlers.finishInputAttempt);
        if (handlers.trackPhoneChange) input.removeEventListener('change', handlers.trackPhoneChange);
        if (handlers.captureOriginalInput) input.removeEventListener('countrychange', formatAndTrackPhoneInput);
        phoneInputHandlers.set(input, {formDataHandler: handlers.formDataHandler});
    }
    input.removeEventListener('focus', startAutofillCheck);
    input.removeEventListener('blur', stopAutofillCheck);
    window.clearInterval(autofillChecks.get(input));
    autofillChecks.delete(input);
    correctionBaselines.delete(input);
    try {
        intlTelInput.getInstance(input)?.destroy();
    } catch {
        // A partially working widget must not keep the plain field unusable.
    }
    input.value = internationalValue;
    phoneValueSnapshots.set(input, internationalValue);
    setPhoneValidity(input);
    const widget = input.closest('.iti');
    if (widget && widget !== input && widget.contains(input)) {
        widget.parentNode?.insertBefore(input, widget);
        widget.remove();
    }
    input.classList?.remove('iti__tel-input');
    const submit = input.closest('.modal-fix-phone')?.querySelector('input[type="submit"]');
    if (submit) {
        submit.disabled = false;
    }
    return country;
}

function supportsFormDataEvent(form) {
    return Boolean(form && 'onformdata' in form);
}

function formatAndTrackPhoneInput(event, captureReplacement = false) {
    const input = event.currentTarget;
    if (input.value !== phoneValueSnapshots.get(input) || ['input', 'change', 'countrychange'].includes(event.type)) {
        setPhoneValidity(input);
    }
    if (captureReplacement) {
        if (input.value !== phoneValueSnapshots.get(input)) {
            recordSubmissionAttempt(input, input.value, event.inputType ?? null);
        }
        captureRawPhoneInput(input, event.inputType ?? null, event.data ?? null);
    }
    formatPhoneInput(event);
    phoneValueSnapshots.set(input, input.value);
}

export function initializeIntlPhones(root) {
    bindFullscreenCountrySelectorViewportListeners();
    root.querySelectorAll('input.intl-phone').forEach(input => {
        if (phoneInputHandlers.has(input)) {
            return;
        }

        let existingInstance = null;
        try {
            existingInstance = intlTelInput.getInstance(input);
        } catch {
            // The widget API itself can fail; the raw input remains usable.
        }
        if (existingInstance) {
            return;
        }

        const formDataHandler = event => {
            try {
                normalizePhoneFormData(input, event);
            } catch {
                enablePlainPhoneFallback(input);
                if (input.name && input.value) {
                    event.formData.set(input.name, input.value);
                }
            }
        };
        if (supportsFormDataEvent(input.form)) {
            input.form.addEventListener('formdata', formDataHandler);
        }
        phoneInputHandlers.set(input, {formDataHandler});

        const initialSubmissionValue = input.value;
        rawPhoneValues.set(input, initialSubmissionValue);
        let instance;
        try {
            instance = intlTelInput(input, phoneOptions(
                input.dataset.phoneCountry,
                input.dataset.phoneLocale,
                input.closest('dialog') ?? document.body,
            ));
            if (!instance || !intlTelInput.getInstance(input)) {
                throw new Error('intl-tel-input did not create an instance');
            }
        } catch {
            input.value = initialSubmissionValue;
            enablePlainPhoneFallback(input);
            return;
        }
        try {
            selectedPhoneCountries.set(input, instance.getSelectedCountry());
        } catch {
            enablePlainPhoneFallback(input);
            return;
        }
        const originalPhoneInput = input.form?.querySelector('input[name="phone_original"], input[name$="[phone_original]"]');
        const correctionWasAutofilledBeforeInit = input.name === 'fixedPhone'
            && input.value !== input.dataset.phoneRenderedValue;
        if (originalPhoneInput && input.value && (input.name !== 'fixedPhone' || correctionWasAutofilledBeforeInit)) {
            originalPhoneInput.value = input.value;
        }
        if (initialSubmissionValue) {
            recordSubmissionAttempt(input, initialSubmissionValue);
        }
        phoneValueSnapshots.set(input, input.value);
        const capturePasteAttempt = event => {
            const pasted = event.clipboardData?.getData('text');
            if (pasted != null) {
                const value = input.value.slice(0, input.selectionStart ?? 0) + pasted
                    + input.value.slice(input.selectionEnd ?? input.value.length);
                rawPhoneValues.set(input, value);
                recordSubmissionAttempt(input, value, 'insertFromPaste');
                pendingInputValues.set(input, value);
            }
        };
        const captureOriginalInput = event => {
            if (event.detail?.isCountryChange) {
                if (pendingInputValues.has(input)) {
                    // The plugin switches countries synchronously inside the same user input event.
                    recordSubmissionAttempt(input, pendingInputValues.get(input));
                } else {
                    submissionAttempts.delete(input);
                }
            } else {
                rawPhoneValues.set(input, event.inputType === 'insertFromPaste' && pendingInputValues.has(input)
                    ? pendingInputValues.get(input) : input.value);
                if (event.inputType !== 'insertFromPaste' || !pendingInputValues.has(input)) {
                    pendingInputValues.set(input, input.value);
                    recordSubmissionAttempt(input, input.value, event.inputType);
                }
                captureRawPhoneInput(input, event.inputType ?? null, event.data ?? null);
            }
        };
        const finishInputAttempt = event => {
            if (!event.detail?.isCountryChange) {
                pendingInputValues.delete(input);
            }
        };
        const trackPhoneChange = event => formatAndTrackPhoneInput(event, true);
        input.addEventListener('input', captureOriginalInput, true);
        input.addEventListener('paste', capturePasteAttempt, true);
        input.addEventListener('input', formatAndTrackPhoneInput);
        input.addEventListener('input', finishInputAttempt);
        input.addEventListener('change', trackPhoneChange);
        input.addEventListener('countrychange', formatAndTrackPhoneInput);
        phoneInputHandlers.set(input, {captureOriginalInput, capturePasteAttempt, finishInputAttempt, trackPhoneChange, formDataHandler});
        input.addEventListener('focus', startAutofillCheck);
        input.addEventListener('blur', stopAutofillCheck);
        formatAndTrackPhoneInput({currentTarget: input}, true);
        if (input.closest('.modal-fix-phone')) {
            correctionBaselines.set(input, phoneSelectionKey(input.value, instance.getSelectedCountry()));
            updateCorrectionSubmit(input, instance.getSelectedCountry());
        }
        void instance.promise.then(() => {
            if (!fallbackPhoneInputs.has(input) && intlTelInput.getInstance(input) === instance) {
                formatAndTrackPhoneInput({currentTarget: input}, true);
            }
        }).catch(() => {});
    });
}

export function destroyIntlPhones(root) {
    root.querySelectorAll('input.intl-phone').forEach(input => {
        const handlers = phoneInputHandlers.get(input);
        if (handlers) {
            if (handlers.captureOriginalInput) input.removeEventListener('input', handlers.captureOriginalInput, true);
            if (handlers.capturePasteAttempt) input.removeEventListener('paste', handlers.capturePasteAttempt, true);
            if (handlers.finishInputAttempt) input.removeEventListener('input', handlers.finishInputAttempt);
            if (handlers.trackPhoneChange) input.removeEventListener('change', handlers.trackPhoneChange);
            if (handlers.captureOriginalInput) input.removeEventListener('input', formatAndTrackPhoneInput);
            if (handlers.captureOriginalInput) input.removeEventListener('countrychange', formatAndTrackPhoneInput);
            if (supportsFormDataEvent(input.form)) {
                input.form.removeEventListener('formdata', handlers.formDataHandler);
            }
            phoneInputHandlers.delete(input);
        }
        setPhoneValidity(input);
        input.removeEventListener('focus', startAutofillCheck);
        input.removeEventListener('blur', stopAutofillCheck);
        window.clearInterval(autofillChecks.get(input));
        autofillChecks.delete(input);
        correctionBaselines.delete(input);
        submissionAttempts.delete(input);
        pendingInputValues.delete(input);
        rawPhoneValues.delete(input);
        phoneValueSnapshots.delete(input);
        fallbackPhoneInputs.delete(input);
        selectedPhoneCountries.delete(input);
        try {
            intlTelInput.getInstance(input)?.destroy();
        } catch {
            // Cleanup must not make a snippet update fail when the widget did.
        }
    });
}

export function formatNationalPhone(value, country, utils = null) {
    const dialCode = country?.dialCode;
    const nationalValue = dialCode && value.trimStart().startsWith(`+${dialCode}`)
        ? value.trimStart().slice(dialCode.length + 1)
        : value;
    const nationalDigits = nationalValue.replace(/\D/g, '');
    const digits = ['cz', 'sk'].includes(country?.iso2) && nationalDigits.length > 1 && nationalDigits.startsWith('0')
        ? nationalDigits.slice(1)
        : nationalDigits;
    if (country?.iso2 === 'cz') {
        return digits.slice(0, digits.startsWith('0') ? 10 : 9).replace(/(\d{3})(?=\d)/g, '$1 ');
    }
    if (!country) {
        return value;
    }
    if (!utils) {
        return ['cz', 'sk'].includes(country.iso2) ? (digits || (nationalDigits.startsWith('0') ? '0' : '')) : nationalValue;
    }
    if (!digits) {
        return nationalDigits.startsWith('0') ? '0' : '';
    }

    const full = `+${country.dialCode}${digits}`;
    const formatted = utils.formatNumberAsYouType(full, country.iso2);
    return formatted.startsWith(`+${country.dialCode}`)
        ? formatted.slice(country.dialCode.length + 1).trimStart()
        : formatted;
}

export function numberWithDialCode(value, country) {
    const digits = formatNationalPhone(value, country).replace(/\D/g, '');
    return country?.dialCode && digits ? `+${country.dialCode}${digits}` : '';
}

function formatPhoneInput(event) {
    const input = event.currentTarget;
    const country = intlTelInput.getInstance(input)?.getSelectedCountry();
    if (!country) {
        return;
    }
    selectedPhoneCountries.set(input, country);
    const cursor = input.selectionStart;
    const dialPrefix = `+${country.dialCode}`;
    const prefixLength = input.value.trimStart().startsWith(dialPrefix) ? country.dialCode.length : 0;
    let digitsBeforeCursor = cursor === null ? null : Math.max(0, input.value.slice(0, cursor).replace(/\D/g, '').length - prefixLength);
    const nationalDigits = input.value.trimStart().startsWith(dialPrefix)
        ? input.value.trimStart().slice(dialPrefix.length).replace(/\D/g, '')
        : input.value.replace(/\D/g, '');
    if (digitsBeforeCursor > 0 && ['cz', 'sk'].includes(country.iso2) && nationalDigits.length > 1 && nationalDigits.startsWith('0')) {
        digitsBeforeCursor--;
    }
    const formatted = formatNationalPhone(input.value, country, intlTelInput.utils);
    if (formatted === input.value) {
        updateCorrectionSubmit(input, country);
        return;
    }
    input.value = formatted;
    if (digitsBeforeCursor !== null && document.activeElement === input) {
        let nextCursor = 0;
        let seenDigits = 0;
        while (nextCursor < formatted.length && seenDigits < digitsBeforeCursor) {
            if (/\d/.test(formatted[nextCursor])) {
                seenDigits++;
            }
            nextCursor++;
        }
        input.setSelectionRange(nextCursor, nextCursor);
    }
    updateCorrectionSubmit(input, country);
}

export function normalizeIntlPhonesForSubmit(form) {
    let hasCountry = true;
    let inputs;
    try {
        inputs = form.querySelectorAll('input.intl-phone');
    } catch {
        allowPhoneFormFallback(form);
        return true;
    }
    inputs.forEach(input => {
        const visibleValue = input.value;
        try {
            const instance = fallbackPhoneInputs.has(input) ? null : intlTelInput.getInstance(input);
            if (!instance) {
                enablePlainPhoneFallback(input);
                return;
            }

            const country = getPhoneCountry(input, instance);
            if (!country?.dialCode) {
                setPhoneValidity(input, missingDialCodeMessages[input.dataset.phoneCountry] ?? missingDialCodeMessages.cz);
                input.focus();
                hasCountry = false;
                return;
            }
            if (!intlTelInput.utils) {
                enablePlainPhoneFallback(input);
                return;
            }

            const attempt = submissionAttempts.get(input);
            const reason = (attempt?.iso2 === country.iso2
                ? attempt.reason ?? phoneSubmissionError(attempt.value, country, intlTelInput.utils) : null)
                ?? phoneSubmissionError(input.value, country, intlTelInput.utils);
            if (reason) {
                setPhoneValidity(input, (submissionMessages[input.dataset.phoneCountry] ?? submissionMessages.cz)[reason]);
                input.focus();
                hasCountry = false;
                return;
            }
            preparePhoneForSubmission(input, country, visibleValue, instance);
        } catch {
            enablePlainPhoneFallback(input);
        }
    });
    return hasCountry;
}

function preparePhoneForSubmission(input, country, visibleValue, instance) {
    setPhoneValidity(input);
    captureRawPhoneInput(input);
    if (!supportsFormDataEvent(input.form) && country?.dialCode) {
        const number = instance && intlTelInput.utils
            ? instance.getNumber(intlTelInput.NUMBER_FORMAT.E164) || numberWithDialCode(visibleValue, country)
            : numberWithDialCode(visibleValue, country);
        if (number) {
            input.value = number;
            phoneValueSnapshots.set(input, number);
            window.setTimeout(() => {
                if (!fallbackPhoneInputs.has(input) && input.value === number) {
                    input.value = visibleValue;
                    phoneValueSnapshots.set(input, visibleValue);
                }
            }, 0);
        }
    }
}

export function allowPhoneFormFallback(form) {
    try {
        form.querySelectorAll('input.intl-phone').forEach(input => enablePlainPhoneFallback(input));
    } catch {
        // A failure in the phone guard itself must never suppress a reaction.
    }
}

export function phoneOptions(country = 'cz', locale = country, fullscreenParent = null) {
    const market = MARKET_PHONE_OPTIONS[country] ? country : 'cz';
    const resolvedLocale = translations[locale] ? locale : marketLocales[market];

    return {
        countryNameLocale: resolvedLocale,
        countryOrder: MARKET_PHONE_OPTIONS[market],
        countrySearch: true,
        countrySelectorMode: 'AUTO',
        formatAsYouType: false,
        fullscreenParent,
        initialCountry: market,
        matchDropdownWidth: true,
        numberDisplayFormat: 'INTERNATIONAL',
        placeholderNumberPolicy: 'AGGRESSIVE',
        separateDialCode: true,
        strictMode: true,
        uiTranslations: translations[resolvedLocale],
    };
}
